/**
 * ProposalRowActions - Menu de ações de uma proposta
 *
 * Compartilhado entre o card do Kanban de Propostas e (na Fase 2) a linha da
 * listagem. Reaproveita exatamente as mesmas RPCs e regras já usadas na aba
 * Propostas do negócio (useDealProposals / useProposalPdf / useProposalShare).
 *
 * @version 1.1.1
 * RELEASE: 2026-09-03 v1.1.1 - Bloqueio do Workflow usa aviso modal global.
 * RELEASE: 2026-08-25 v1.1.0 - Link público respeita a revogação: 'Copiar link público'
 *          só com link ativo e 'Gerar link público' apenas em situações compartilháveis
 *          (some em substituída/cancelada/recusada/expirada/aceita)
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import React, { useState } from 'react';
import {
  MoreHorizontal,
  Eye,
  FileText,
  Link2,
  Send,
  CheckCircle2,
  XCircle,
  Ban,
  Briefcase,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useDealProposals,
  useProposalPdf,
  useProposalShare,
  buildPublicProposalUrl,
} from '@/hooks/useDealProposals';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';
import type { KanbanProposal } from '@/hooks/useProposalKanbanColumn';

interface ProposalRowActionsProps {
  /** Proposta alvo das ações */
  proposal: KanbanProposal;
  /** Abre o modal de detalhes da proposta */
  onViewDetails: () => void;
  /** Abre o negócio vinculado */
  onViewDeal?: () => void;
  /** Callback após qualquer ação concluída (invalida colunas/contagens) */
  onActionComplete?: () => void;
  /** Classe extra do gatilho */
  triggerClassName?: string;
}

export function ProposalRowActions({
  proposal,
  onViewDetails,
  onViewDeal,
  onActionComplete,
  triggerClassName,
}: ProposalRowActionsProps) {
  const { canEdit: canEditMenu, canDelete } = usePermissions();
  // dealId nulo: só precisamos das mutations, a listagem fica desabilitada
  const { updateStatus } = useDealProposals(null, false);
  const { generatePdf, openPdf } = useProposalPdf();
  const { sendProposal } = useProposalShare();
  const workflowNotice = useWorkflowGuardNotice();

  // Transição destrutiva pendente de confirmação
  const [pendingStatus, setPendingStatus] = useState<'rejected' | 'cancelled' | null>(null);
  const [busy, setBusy] = useState(false);

  // Permissões (mesmas chaves já usadas na aba Propostas do negócio)
  const canEdit = canEditMenu('crm_proposals');
  const canCancelAccepted = canDelete('crm_proposals_cancel_accepted');

  const status = proposal.status;
  const isFinal = ['accepted', 'rejected', 'expired', 'cancelled', 'superseded'].includes(status);
  // v1.1.0: link público só existe quando ativo (revogado em substituída/cancelada)
  const hasActiveLink = !!proposal.public_enabled && !!proposal.public_token;
  // Gerar link só faz sentido enquanto a proposta pode ser compartilhada
  const canGenerateLink = ['draft', 'generated', 'sent', 'viewed'].includes(status);


  /** Executa transição simples (enviar/aceitar) */
  const handleStatus = async (next: 'sent' | 'accepted') => {
    setBusy(true);
    try {
      await updateStatus.mutateAsync({ proposalId: proposal.id, status: next });
      onActionComplete?.();
    } finally {
      setBusy(false);
    }
  };

  /** Confirma recusa/cancelamento */
  const handleConfirmDestructive = async () => {
    if (!pendingStatus) return;
    setBusy(true);
    try {
      await updateStatus.mutateAsync({ proposalId: proposal.id, status: pendingStatus });
      onActionComplete?.();
    } finally {
      setBusy(false);
      setPendingStatus(null);
    }
  };

  /** Abre o PDF (gera quando ainda não existe) */
  const handlePdf = async () => {
    setBusy(true);
    try {
      if (proposal.pdf_storage_path) {
        await openPdf(proposal.pdf_storage_path);
      } else {
        await generatePdf.mutateAsync(proposal.id);
      }
      onActionComplete?.();
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível abrir o PDF');
    } finally {
      setBusy(false);
    }
  };

  /** Copia o link público existente ou gera um novo */
  const handleLink = async () => {
    setBusy(true);
    try {
      const url = hasActiveLink
        ? buildPublicProposalUrl(proposal.public_token!)
        : (await sendProposal.mutateAsync({ proposalId: proposal.id })).url;
      await navigator.clipboard.writeText(url);
      toast.success('Link público copiado');
      onActionComplete?.();
    } catch (error: any) {
      // onError do hook já consome o aviso; a chamada abaixo evita toast duplicado.
      if (!workflowNotice.showWorkflowGuardNotice(error)) {
        toast.error(error?.message || 'Não foi possível gerar o link');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('h-7 w-7', triggerClassName)}
            onClick={(e) => e.stopPropagation()}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <MoreHorizontal className="h-3.5 w-3.5" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={onViewDetails}>
            <Eye className="h-3.5 w-3.5 mr-2" />
            Ver proposta
          </DropdownMenuItem>

          {onViewDeal && proposal.deal_id && (
            <DropdownMenuItem onClick={onViewDeal}>
              <Briefcase className="h-3.5 w-3.5 mr-2" />
              Ver negócio
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={handlePdf}>
            <FileText className="h-3.5 w-3.5 mr-2" />
            {proposal.pdf_storage_path ? 'Abrir PDF' : 'Gerar PDF'}
          </DropdownMenuItem>

          {/* Copiar: só com link ATIVO · Gerar: só quando ainda é compartilhável */}
          {canEdit && hasActiveLink && (
            <DropdownMenuItem onClick={handleLink}>
              <Link2 className="h-3.5 w-3.5 mr-2" />
              Copiar link público
            </DropdownMenuItem>
          )}
          {canEdit && !hasActiveLink && canGenerateLink && (
            <DropdownMenuItem onClick={handleLink}>
              <Link2 className="h-3.5 w-3.5 mr-2" />
              Gerar link público
            </DropdownMenuItem>
          )}

          {canEdit && !isFinal && (
            <>
              <DropdownMenuSeparator />
              {['draft', 'generated'].includes(status) && (
                <DropdownMenuItem onClick={() => handleStatus('sent')}>
                  <Send className="h-3.5 w-3.5 mr-2" />
                  Marcar como enviada
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleStatus('accepted')}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                Marcar como aceita
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setPendingStatus('rejected')}
              >
                <XCircle className="h-3.5 w-3.5 mr-2" />
                Marcar como recusada
              </DropdownMenuItem>
            </>
          )}

          {/* Cancelar: proposta aceita exige a permissão específica */}
          {canEdit &&
            !['cancelled', 'superseded'].includes(status) &&
            (status !== 'accepted' || canCancelAccepted) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setPendingStatus('cancelled')}
                >
                  <Ban className="h-3.5 w-3.5 mr-2" />
                  Cancelar proposta
                </DropdownMenuItem>
              </>
            )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmação das transições destrutivas */}
      <AlertDialog open={!!pendingStatus} onOpenChange={(open) => !open && setPendingStatus(null)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingStatus === 'rejected' ? 'Recusar proposta' : 'Cancelar proposta'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingStatus === 'rejected'
                ? `A proposta ${proposal.proposal_number} será marcada como recusada.`
                : `A proposta ${proposal.proposal_number} será cancelada e não poderá mais ser aceita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDestructive}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
